import { Button, Checkbox, Grid, List, ListItem, ListItemText, Snackbar, TextField, Typography, Pagination } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as React from "react";
import { useLocation } from "react-router-dom";
import type { Student } from "../../../services/models/Student";
import apiClient from "../../../utils/api";
import { PendingUser } from "../../../services";

export const ClassEnroll: React.FC = () => {
  const location = useLocation();
  const classID = location.pathname.split("/").pop() || "";

  const [enrollEmails, setEnrollEmails] = React.useState("");
  const [invalidEnrollEmails, setInvalidEnrollEmails] = React.useState<string[]>([]);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState<"success" | "error">("success"); // New state

  const [admins, setAdmins] = React.useState<Student[]>([]); // Admin users
  const [students, setStudents] = React.useState<Student[]>([]); // Student users
  const [pendingUsers, setPendingUsers] = React.useState<PendingUser[]>([]);  // Pending users

  // Separate state for admins and students and pending Users
  const [selectedAdmins, setSelectedAdmins] = React.useState<string[]>([]);
  const [selectAllAdmins, setSelectAllAdmins] = React.useState(false);

  const [selectedStudents, setSelectedStudents] = React.useState<string[]>([]);
  const [selectAllStudents, setSelectAllStudents] = React.useState(false);

  const [selectedPending, setSelectedPending] = React.useState<string[]>([]);
  const [selectAllPending, setSelectAllPending] = React.useState(false);

  // Error messages for invalid emails
  const [enrollErrorMessage, setEnrollErrorMessage] = React.useState<string>("");

    // New search and pagination states
  const [adminSearch, setAdminSearch] = React.useState("");
  const [studentSearch, setStudentSearch] = React.useState("");
  const [pendingSearch, setPendingSearch] = React.useState("");

  const [adminPage, setAdminPage] = React.useState(1);
  const [studentPage, setStudentPage] = React.useState(1);
  const [pendingPage, setPendingPage] = React.useState(1);

  const pageSize = 15; // Page size of 15

   // Filtered data based on search query and pagination
   const filteredAdmins = admins
   .filter((admin) => admin.email.toLowerCase().includes(adminSearch.toLowerCase()))
   .slice((adminPage - 1) * pageSize, adminPage * pageSize);

  const filteredStudents = students
    .filter((student) => student.email.toLowerCase().includes(studentSearch.toLowerCase()))
    .slice((studentPage - 1) * pageSize, studentPage * pageSize);

  const filteredPendingUsers = pendingUsers
    .filter((user) => user.userEmail.toLowerCase().includes(pendingSearch.toLowerCase()))
    .slice((pendingPage - 1) * pageSize, pendingPage * pageSize);


  const handleSelectPending = (userId: string) => {
    setSelectedPending((prevSelected) =>
      prevSelected.includes(userId) ? prevSelected.filter((id) => id !== userId) : [...prevSelected, userId]
    );
  };

  const handleSelectAllPending = () => {
    if (selectAllPending) {
      setSelectedPending([]); // Deselect all pending users
    } else {
      setSelectedPending(pendingUsers.map((user) => user.id)); // Select all pending users
    }
    setSelectAllPending(!selectAllPending);
  };

  const handleDeleteSelectedPending = async () => {
    try {
      await apiClient.classControllerDeletePendingUsers({
        classDeletePendingEnrollmentRequest: { classId: classID, pendingUserIds: selectedPending }
      });
      setSnackbarSeverity("success");
      setSnackbarMessage("Pending users deleted successfully!");
      setOpenSnackbar(true);
      const remainingPendingUsers = pendingUsers.filter((user) => !selectedPending.includes(user.id));
      setPendingUsers(remainingPendingUsers); // Remove selected pending users
      setSelectedPending([]);
      setSelectAllPending(false);
    } catch (error) {
      setSnackbarSeverity("error");
      setSnackbarMessage("Failed to delete pending users. Please try again.");
      setOpenSnackbar(true);
    }
  };

  const processEmails = (emailInput: string): { validEmails: string[], invalidEmails: string[] } => {
    const emails = emailInput.split(",").map(email => email.trim());
    const validEmails: string[] = [];
    const invalidEmails: string[] = [];

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Basic email validation regex

    emails.forEach(email => {
      if (emailRegex.test(email)) {
        validEmails.push(email);
      } else if (email) {
        invalidEmails.push(email);
      }
    });

    return { validEmails, invalidEmails };
  };

  const handleEnroll = async () => {
    const { validEmails, invalidEmails } = processEmails(enrollEmails);

    setInvalidEnrollEmails(invalidEmails);

    if (invalidEmails.length > 0) {
      setEnrollErrorMessage("Invalid emails provided: " + invalidEmails.join(", "));
      return;
    } else {
      setEnrollErrorMessage(""); // Clear error message if no invalid emails
    }
    console.log("Enrolling users with emails:", validEmails);

    try {
      const res = await apiClient.classControllerEnroll({
        classEnrollRequest: {classId: classID, studentEmails: validEmails }
      });
      // Assuming that the API responds with a 'success' property
      if (res) {
        setSnackbarSeverity("success");
        setSnackbarMessage("Users enrolled successfully!");
        setOpenSnackbar(true);
        setEnrollEmails("");
        fetchRoster();
        fetchPendingUsers();
      } else {
        setSnackbarSeverity("error");
        setSnackbarMessage("Failed to enroll users. Please try again.");
        setOpenSnackbar(true);
      }
    } catch (error: any) {
      console.error("Error enrolling users:", error);
    // Check for a 409 Conflict error
    if (error.message.includes("ConflictException")) {
      setSnackbarSeverity("error");
      setSnackbarMessage(error.message);
    } else {
      setSnackbarSeverity("error");
      setSnackbarMessage("Unexpected error occurred. Please try again.");
    }
    setOpenSnackbar(true);
    }
  };
// Handle individual admin selection
const handleSelectAdmin = (userId: string) => {
  setSelectedAdmins((prevSelected) =>
    prevSelected.includes(userId)
      ? prevSelected.filter((id) => id !== userId) // Deselect
      : [...prevSelected, userId] // Select
  );
};

// Handle "Select All" functionality for admins
const handleSelectAllAdmins = () => {
  if (selectAllAdmins) {
    setSelectedAdmins([]); // Deselect all admins
  } else {
    setSelectedAdmins(admins.map((admin) => admin.userId)); // Select all admins
  }
  setSelectAllAdmins(!selectAllAdmins); // Toggle select all state
};

// Handle individual student selection
const handleSelectStudent = (userId: string) => {
  setSelectedStudents((prevSelected) =>
    prevSelected.includes(userId)
      ? prevSelected.filter((id) => id !== userId) // Deselect
      : [...prevSelected, userId] // Select
  );
};

// Handle "Select All" functionality for students
const handleSelectAllStudents = () => {
  if (selectAllStudents) {
    setSelectedStudents([]); // Deselect all students
  } else {
    setSelectedStudents(students.map((student) => student.userId)); // Select all students
  }
  setSelectAllStudents(!selectAllStudents); // Toggle select all state
};

// Handle delete action for admins
const handleDeleteSelectedAdmins = async () => {
  try {
    await apiClient.classControllerDelete({
      classEnrollDeleteRequest: { classId: classID, studentIds: selectedAdmins }
    });
    setSnackbarSeverity("success");
    setSnackbarMessage("Admins deleted successfully!");
    setOpenSnackbar(true);
    const remainingAdmins = admins.filter((admin) => !selectedAdmins.includes(admin.userId));
    setAdmins(remainingAdmins); // Remove selected admins from the list
    setSelectedAdmins([]); // Clear selected admins
    setSelectAllAdmins(false); // Reset "Select All"
  } catch (error) {
    setSnackbarSeverity("error");
    setSnackbarMessage("Failed to delete admins. Please try again.");
    setOpenSnackbar(true);
  }
};

// Handle delete action for students
const handleDeleteSelectedStudents = async () => {
  try {
    await apiClient.classControllerDelete({
      classEnrollDeleteRequest: { classId: classID, studentIds: selectedStudents }
    });
    setSnackbarSeverity("success");
    setSnackbarMessage("Students deleted successfully!");
    setOpenSnackbar(true);
    const remainingStudents = students.filter((student) => !selectedStudents.includes(student.userId));
    setStudents(remainingStudents); // Remove selected students from the list
    setSelectedStudents([]); // Clear selected students
    setSelectAllStudents(false); // Reset "Select All"
  } catch (error) {
    setSnackbarSeverity("error");
    setSnackbarMessage("Failed to delete students. Please try again.");
    setOpenSnackbar(true);
  }
};
const fetchRoster = async () => {
  try {
    console.log("fetching roster.... ")
    // Fetch class roster logic
    const res = await apiClient.classControllerRoster({
      classRosterRequest: {classId: classID}
    })
    console.log("roster: ", res)
    const adminUsers = res.users.filter((user: Student) => user.role === "admin");
    const studentUsers = res.users.filter((user: Student) => user.role !== "admin");
    setAdmins(adminUsers);
    setStudents(studentUsers);
  } catch (error) {
    console.log("error fetching roster", error);
  }
};

const fetchPendingUsers = async () => {
  try {
    const res = await apiClient.classControllerGetPendingEnrollment({
      classPendingEnrollmentRequest: {classId: classID}
    })
    console.log("pending users: ", res)
    const pendingUsers = res.pendingUsers;
    setPendingUsers(pendingUsers)
  } catch (error) {
    console.log("error fetching roster", error);
  }
}
  React.useEffect(() => {
    const fetchRoster = async () => {
      try {
        console.log("fetching roster.... ")
        // Fetch class roster logic
        const res = await apiClient.classControllerRoster({
          classRosterRequest: {classId: classID}
        })
        console.log("roster: ", res)
        const adminUsers = res.users.filter((user: Student) => user.role === "admin");
        const studentUsers = res.users.filter((user: Student) => user.role !== "admin");
        setAdmins(adminUsers);
        setStudents(studentUsers);
      } catch (error) {
        console.log("error fetching roster", error);
      }
    };
    const fetchPendingUsers = async () => {
      try {
        const res = await apiClient.classControllerGetPendingEnrollment({
          classPendingEnrollmentRequest: {classId: classID}
        })
        console.log("pending users: ", res)
        const pendingUsers = res.pendingUsers;
        setPendingUsers(pendingUsers)
      } catch (error) {
        console.log("error fetching roster", error);
      }
    }
    fetchRoster();
    fetchPendingUsers();
  }, [classID]);

  return (
    <div style={{ paddingBottom: '50px' }}>
      <h1>Class Enrollment</h1>
      <Grid container spacing={2}>
        {/* Input for enrolling users */}
        <Grid item xs={8}>
          <TextField
            label="Emails to Enroll (comma separated)"
            variant="outlined"
            fullWidth
            value={enrollEmails}
            onChange={(e) => setEnrollEmails(e.target.value)}
            inputProps={{ style: { fontSize: 18 } }} // Larger text size
            multiline
            rows={3} // To handle multiple email entries
            error={invalidEnrollEmails.length > 0} // Show error state if there are invalid emails
          />
          {/* Display invalid email error message for enrollment */}
          {enrollErrorMessage && (
            <Typography color="error" variant="body2">
              {enrollErrorMessage}
            </Typography>
          )}
        </Grid>
        <Grid item xs={4}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleEnroll}
          >
            Enroll Users
          </Button>
        </Grid>
      </Grid>

      <h2>Pending Enrolled Users</h2>
      <Grid container alignItems="center" spacing={0} sx={{ marginBottom: '16px' }}>
        <Grid item xs={0}>
          <Checkbox
            checked={selectAllPending}
            onChange={handleSelectAllPending}
          />
        </Grid>
        <Grid item xs={0}>
          <Typography variant="body1" sx={{ marginRight: '8px' }}>
            Select All
          </Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Button
            variant="contained"
            sx={{ backgroundColor: 'darkred', color: 'white', '&:hover': { backgroundColor: 'darkred' } }}
            onClick={handleDeleteSelectedPending}
          >
            Delete Selected
          </Button>
        </Grid>
        <Grid item xs={2}>
          <TextField
            label="Search Pending Users"
            variant="outlined"
            fullWidth
            value={pendingSearch}
            onChange={(e) => setPendingSearch(e.target.value)}
            size="small"
          />
        </Grid>
      </Grid>
      <List>
        {filteredPendingUsers.map((user) => (
          <ListItem
            key={user.id}
            divider
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '300px', height: "30px" }}
          >
            <Checkbox
              checked={selectedPending.includes(user.id)}
              onChange={() => handleSelectPending(user.id)}
            />
            <ListItemText
        primary={
          <>
            {user.userEmail}
            {user.role === "admin" && (
              <Typography component="span" sx={{ color: 'orange', marginLeft: '8px', fontWeight: 'bold' }}>
                (Admin)
              </Typography>
            )}
          </>
        }
      />
          </ListItem>
        ))}
      </List>
      <Pagination
        count={Math.ceil(pendingUsers.filter((user) => user.userEmail.toLowerCase().includes(pendingSearch.toLowerCase())).length / pageSize)}
        page={pendingPage}
        onChange={(_, value) => setPendingPage(value)}
      />

      <h2>Admins / Instructors</h2>
      <Grid container alignItems="center" spacing={0} sx={{ marginBottom: '16px' }}>
        {/* Select All Checkbox */}
        <Grid item xs={0}>
          <Checkbox
            checked={selectAllAdmins}
            onChange={handleSelectAllAdmins}
          />
        </Grid>
        <Grid item xs={0}>
          <Typography variant="body1" sx={{ marginRight: '8px' }}>
            Select All
          </Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Button
            variant="contained"
            sx={{ backgroundColor: 'darkred', color: 'white', '&:hover': { backgroundColor: 'darkred' } }}
            onClick={handleDeleteSelectedAdmins}
          >
            Delete Selected
          </Button>
        </Grid>
        <Grid item xs={2}>
          <TextField
            label="Search Admins"
            variant="outlined"
            fullWidth
            value={adminSearch}
            onChange={(e) => setAdminSearch(e.target.value)}
            size="small"
          />
        </Grid>
      </Grid>
      {/* List of Students with Individual Checkboxes */}
      <List>
        {filteredAdmins.map((admin) => (
          <ListItem
            key={admin.userId}
            divider
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '300px', height: "30px" }}
          >
            <Checkbox
              checked={selectedAdmins.includes(admin.userId)}
              onChange={() => handleSelectAdmin(admin.userId)}
            />
            <ListItemText primary={admin.email} />
          </ListItem>
        ))}
      </List>
      <Pagination
        count={Math.ceil(admins.filter((admin) => admin.email.toLowerCase().includes(adminSearch.toLowerCase())).length / pageSize)}
        page={adminPage}
        onChange={(_, value) => setAdminPage(value)}
      />

<h2>Students</h2>
      <Grid container alignItems="center" spacing={0} sx={{ marginBottom: '16px' }}>
        {/* Select All Checkbox */}
        <Grid item xs={0}>
          <Checkbox
            checked={selectAllStudents}
            onChange={handleSelectAllStudents}
          />
        </Grid>
        <Grid item xs={0}>
          <Typography variant="body1" sx={{ marginRight: '8px' }}>
            Select All
          </Typography>
        </Grid>
        <Grid item xs={1.5}>
          <Button
            variant="contained"
            sx={{ backgroundColor: 'darkred', color: 'white', '&:hover': { backgroundColor: 'darkred' } }}
            onClick={handleDeleteSelectedStudents}
          >
            Delete Selected
          </Button>
        </Grid>
        <Grid item xs={2}>
          <TextField
            label="Search Students"
            variant="outlined"
            fullWidth
            value={studentSearch}
            onChange={(e) => setStudentSearch(e.target.value)}
            size="small"
          />
        </Grid>
      </Grid>
      {/* List of Students with Individual Checkboxes */}
      <List>
        {filteredStudents.map((student) => (
          <ListItem
            key={student.userId}
            divider
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '300px', height: "30px" }}
          >
            <Checkbox
              checked={selectedStudents.includes(student.userId)}
              onChange={() => handleSelectStudent(student.userId)}
            />
            <ListItemText primary={student.email} />
          </ListItem>
        ))}
      </List>
      <Pagination
        count={Math.ceil(students.filter((student) => student.email.toLowerCase().includes(studentSearch.toLowerCase())).length / pageSize)}
        page={studentPage}
        onChange={(_, value) => setStudentPage(value)}
      />

      {/* Snackbar for success notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
      >
        <MuiAlert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
